import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { useMemo, useRef } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AddEditDocsRestrictedDialog from "../../../components/dialog/AddEditDocsRestrictedDialog/AddEditDocsRestrictedDialog";
import { useDocsTableConfig } from "../../../components/table/DocsTable/TablesConfig";
import FilterTable from "../../../components/table/FilterTable";
import LoaderWrapper from "../../../components/wrapper/LoaderWrapper";
import { useAuth } from "../../../context/auth";
import { DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER } from "../../../helpers/constants";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import useDialog from "../../../hooks/useDialog";
import {
    documentsApi,
    useDeleteDocumentFromAllProducts,
    useProductCards,
} from "../../../services/documentsService";
import TableService from "../../../services/tableService";

export const ProductCards = (props) => {
    const { t } = useTranslation();
    const { product } = props;
    const { user } = useAuth();
    const axiosPrivate = useAxiosPrivate();

    const [openAddFileDialog, handleOpenAddFileDialog, handleCloseAddFileDialog] = useDialog();

    const cardTypeRef = useRef();

    const handleOpenAddCardJpgDialog = () => {
        cardTypeRef.current = "jpg";
        handleOpenAddFileDialog();
    };

    const handleOpenAddCardPdfDialog = () => {
        cardTypeRef.current = "pdf";
        handleOpenAddFileDialog();
    };

    const {
        data: documents,
        isLoading,
        error,
        refetch,
    } = useProductCards(product?.id || "", {
        enabled: !!product?.id,
    });

    const { getTableProps, fetchingFromBackend } = useDocsTableConfig();

    const deleteMutation = useDeleteDocumentFromAllProducts({
        onSuccess: () => {
            toast.success(t("documents.document_deleted_successfully"));
            refetch();
        },
        onError: () => {
            toast.error(t("snackbar_alert.occurred_error_during_delete"));
        },
    });

    const onPreview = async (docId) => {
        try {
            await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_preview"));
        }
    };

    const onDownloadFile = async (docId) => {
        try {
            await documentsApi.handleDownloadDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_download"));
        }
    };

    const onDeleteFile = (docId) => {
        deleteMutation.mutate({ document_id: docId });
    };

    const tableProps = useMemo(() => {
        if (fetchingFromBackend) {
            return undefined;
        }
        return getTableProps({
            excludedFields: [
                "manage_products",
                "investment",
                "docs_investant",
                "docs_industry",
                "docs_restricted_user",
                "docs_restricted_role",
                "created",
                "creator",
                "docsinve_is_prospectus",
            ],
        });
    }, [fetchingFromBackend, getTableProps]);

    const transformedDocsData = useMemo(() => {
        if (!documents) {
            return [];
        }

        return documents.map((doc) => ({
            ...doc,
            related_products: product ? [product.id] : [],
        }));
    }, [documents, product]);

    const preparedDocsData = useMemo(() => {
        if (!tableProps?.tableConfig || !transformedDocsData) {
            return undefined;
        }

        return TableService.getPreparedDataForCollapsibleTable(
            tableProps.tableConfig,
            transformedDocsData,
            [],
            [],
            onPreview,
            onDownloadFile,
            onDeleteFile,
            false,
            // readOnly
            [],
            refetch,
            false,
            // showEditButton
            true,
            // showRevisionButton
            true,
            // showStandards,
        );
    }, [tableProps?.tableConfig, transformedDocsData, refetch]);

    const showAddCardJpgButton = documents?.filter((d) => d.is_card_jpg)?.length === 0;
    const showAddCardPdfButton = documents?.filter((d) => d.is_card_pdf)?.length === 0;

    const renderContent = () => {
        if (!product) {
            return (
                <Typography sx={{ p: 2, textAlign: "center" }}>
                    {t(
                        "products.ProductDocuments.multipleProductsSelected",
                        "Document management is available only when a single product is selected.",
                    )}
                </Typography>
            );
        }

        if (isLoading) {
            return <CircularProgress sx={{ alignSelf: "center", mt: 2 }} />;
        }

        if (error) {
            return (
                <Typography color="error" sx={{ p: 2, textAlign: "center" }}>
                    {t("products.ProductDocuments.error", "Error loading documents")}
                </Typography>
            );
        }

        return (
            <LoaderWrapper showLoader={isLoading || preparedDocsData === undefined}>
                {tableProps && (
                    <FilterTable
                        // @ts-ignore
                        data={preparedDocsData || []}
                        withPagination={false}
                        style={{ maxHeight: "70vh" }}
                        showCleanFilterIcon={false}
                        showContextMenu={false}
                        showExportToFileButton={false}
                        headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                            tableProps.tableConfig,
                            { loading: false, value: {} },
                            [],
                            [],
                        )}
                        showDetailsIcon={false}
                        showCounterRecords={false}
                        showCheckbox={false}
                        showFilters={false}
                        filterPrefix={tableProps.tableFilterPrefix}
                        setSelectedRowId={() => {}}
                        hideFiltersIconCol={true}
                    />
                )}
            </LoaderWrapper>
        );
    };

    return (
        <Paper
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                margin: "1rem",
                padding: "1rem",
            }}
        >
            <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
                <Typography variant="h3" align="left" sx={{ width: "100%", fontSize: "1.5rem" }}>
                    {t("products.ProductDocuments.cards")}
                </Typography>
                {showAddCardJpgButton && (
                    <Button
                        startIcon={<AddCircleIcon />}
                        variant="contained"
                        onClick={handleOpenAddCardJpgDialog}
                        disabled={!product}
                        sx={{ whiteSpace: "nowrap", paddingInline: 3 }}
                    >
                        {t("products.ProductDocuments.addCardJpg")}
                    </Button>
                )}
                {showAddCardPdfButton && (
                    <Button
                        startIcon={<AddCircleIcon />}
                        variant="contained"
                        onClick={handleOpenAddCardPdfDialog}
                        disabled={!product}
                        sx={{ whiteSpace: "nowrap", paddingInline: 3, marginLeft: 2 }}
                    >
                        {t("products.ProductDocuments.addCardPdf")}
                    </Button>
                )}
            </Box>
            <Box sx={{ width: "100%", minHeight: "100px" }}>{renderContent()}</Box>
            {openAddFileDialog && (
                <AddEditDocsRestrictedDialog
                    open={openAddFileDialog}
                    onClose={handleCloseAddFileDialog}
                    onSubmit={() => {
                        handleCloseAddFileDialog();
                        refetch();
                    }}
                    request_user={user?.username || ""}
                    multiple={false}
                    showIndustry={true}
                    availableDocsRestrictedTypes={[DOCS_RESTRICTED_USER, DOCS_RESTRICTED_ROLE]}
                    readOnly={false}
                    filesOnMemory={false}
                    isRevisionMode={false}
                    product_ids={product ? [product.id] : []}
                    isCardJpg={cardTypeRef.current === "jpg"}
                    isCardPdf={cardTypeRef.current === "pdf"}
                />
            )}
        </Paper>
    );
};
